import React, { useState } from 'react';
import {
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsOff as NotificationsOffIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { usePushNotifications } from './usePushNotifications';

interface PushNotificationManagerProps {
  onSubscriptionChange?: (isSubscribed: boolean) => void;
}

const PushNotificationManager: React.FC<PushNotificationManagerProps> = ({
  onSubscriptionChange
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);
  
  const {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    permission,
    subscribe,
    unsubscribe,
    requestPermission
  } = usePushNotifications();

  // Обработка изменения подписки
  React.useEffect(() => {
    if (onSubscriptionChange) {
      onSubscriptionChange(isSubscribed);
    }
  }, [isSubscribed, onSubscriptionChange]);

  const handleSubscribe = async () => {
    const success = await subscribe();
    if (success) {
      setDialogOpen(false);
    }
  };

  const handleUnsubscribe = async () => {
    await unsubscribe();
    setDialogOpen(false);
  };

  const getStatusText = () => {
    if (!isSupported) return 'Не поддерживается';
    if (permission === 'denied') return 'Заблокировано';
    if (isSubscribed) return 'Включены';
    return 'Выключены';
  };

  const getStatusColor = (): 'success' | 'warning' | 'error' | 'info' => {
    if (!isSupported || permission === 'denied') return 'error';
    if (isSubscribed) return 'success';
    return 'warning';
  };

  const renderDialogContent = () => {
    if (!isSupported) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          Ваш браузер не поддерживает push-уведомления. 
          Попробуйте использовать современный браузер (Chrome, Firefox, Safari).
        </Alert>
      );
    }

    if (permission === 'denied') {
      return (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Уведомления заблокированы в настройках браузера. 
          Разрешите уведомления для этого сайта в настройках браузера.
        </Alert>
      );
    }

    return (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {isSubscribed 
            ? 'Вы подписаны на уведомления о новых документах.'
            : 'Получайте уведомления о новых документах даже при закрытом браузере.'}
        </Typography>

        {!isSubscribed && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Push-уведомления работают через защищенное соединение. 
              Нажмите "Включить уведомления" для подписки.
            </Typography>
          </Alert>
        )}

        <Collapse in={infoExpanded}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Как это работает:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Уведомления приходят автоматически при появлении новых документов</li>
                <li>Работает даже при закрытом браузере</li>
                <li>Вы можете отключить в любое время</li>
                <li>Никаких спам-сообщений - только важные документы</li>
              </ul>
            </Typography>
          </Alert>
        </Collapse>

        <Button
          startIcon={<InfoIcon />}
          onClick={() => setInfoExpanded(!infoExpanded)}
          sx={{ mb: 2 }}
          size="small"
        >
          {infoExpanded ? 'Скрыть' : 'Подробнее'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Кнопка управления уведомлениями */}
      <Tooltip title={`Push-уведомления: ${getStatusText()}`}>
        <IconButton
          onClick={() => setDialogOpen(true)}
          color={isSubscribed ? 'primary' : 'default'}
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : isSubscribed ? (
            <NotificationsIcon />
          ) : (
            <NotificationsOffIcon />
          )}
        </IconButton>
      </Tooltip>

      {/* Диалог настройки уведомлений */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          Push-уведомления
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            onClick={() => setDialogOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {renderDialogContent()}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {isSupported && permission !== 'denied' && (
            <>
              {isSubscribed ? (
                <Button
                  onClick={handleUnsubscribe}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} /> : <NotificationsOffIcon />}
                  color="warning"
                >
                  Отключить уведомления
                </Button>
              ) : (
                <Button
                  onClick={handleSubscribe}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} /> : <NotificationsIcon />}
                  variant="contained"
                >
                  Включить уведомления
                </Button>
              )}
            </>
          )}
          
          <Button 
            onClick={() => setDialogOpen(false)}
            disabled={isLoading}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PushNotificationManager;
